import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const t = localStorage.getItem("pos_token");
    if (!t) {
      setUser(null);
      setTenant(null);
      setSubscription(null);
      setPlan(null);
      setEntitlements(null);
      setLoading(false);
      return;
    }
    try {
      const r = await api.get("/auth/me");
      setUser(r.data.user);
      setTenant(r.data.tenant);
      setSubscription(r.data.subscription);
      setPlan(r.data.plan || null);
      setEntitlements(r.data.entitlements || null);
    } catch {
      localStorage.removeItem("pos_token");
      setUser(null);
      setTenant(null);
      setSubscription(null);
      setPlan(null);
      setEntitlements(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (identifier, password) => {
    const r = await api.post("/auth/login", { identifier, password });
    localStorage.setItem("pos_token", r.data.token);
    setUser(r.data.user);
    setTenant(r.data.tenant);
    await refresh();
    return r.data.user;
  };

  const register = async (data) => {
    const r = await api.post("/auth/register", data);
    localStorage.setItem("pos_token", r.data.token);
    setUser(r.data.user);
    setTenant(r.data.tenant);
    await refresh();
    return r.data.user;
  };

  const logout = () => {
    localStorage.removeItem("pos_token");
    setUser(null);
    setTenant(null);
    setSubscription(null);
    setPlan(null);
    setEntitlements(null);
    window.location.href = "/login";
  };

  const hasPerm = (permission) => {
    if (!user) return false;
    if (user.role === "platform_owner" || user.role === "admin") return true;
    const perms = user.permissions || [];
    return perms.includes("*") || perms.includes(permission);
  };

  const hasFeature = (featureKey) => {
    if (!featureKey) return true;
    if (!user) return false;
    if (user.role === "platform_owner") return true;
    if (!entitlements?.available) return false;
    return (entitlements.feature_keys || []).includes(featureKey);
  };

  return (
    <AuthCtx.Provider value={{
      user,
      tenant,
      subscription,
      plan,
      entitlements,
      loading,
      login,
      register,
      logout,
      refresh,
      hasPerm,
      hasFeature,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
